import PropTypes from "prop-types";
// @mui
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
// components
import Iconify from "src/components/iconify";

function Item({ icon, label, onClick, selected, disabled }) {
  return (
    <MenuItem
      dense
      onClick={onClick}
      selected={selected}
      disabled={disabled}
    >
      <Iconify
        icon={icon}
        width={18}
        sx={{ mr: 2, flexShrink: 0 }}
      />
      {label}
    </MenuItem>
  );
}

Item.propTypes = {
  icon: PropTypes.string,
  label: PropTypes.string,
  onClick: PropTypes.func,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default function ColumnMenu({ target, ctx, onClose }) {
  const column = target?.column;
  const columnState = column ? ctx.getColumnState(column) : null;
  const sections = [];

  const run = action => () => {
    onClose();
    action();
  };

  if (column) {
    if (ctx.sortingMode !== "none" && column.allowSorting)
      sections.push([
        <Item
          key="asc"
          icon="eva:arrow-upward-fill"
          label={ctx.t("gridSortAscending")}
          selected={columnState?.sortOrder === "asc"}
          onClick={run(() => ctx.setSort(column, "asc"))}
        />,
        <Item
          key="desc"
          icon="eva:arrow-downward-fill"
          label={ctx.t("gridSortDescending")}
          selected={columnState?.sortOrder === "desc"}
          onClick={run(() => ctx.setSort(column, "desc"))}
        />,
        <Item
          key="clear"
          icon="mdi:sort-variant-remove"
          label={ctx.t("gridClearSorting")}
          disabled={!columnState?.sortOrder}
          onClick={run(() => ctx.setSort(column, null))}
        />,
      ]);

    if (ctx.groupingEnabled && column.allowGrouping)
      sections.push(
        [
          typeof columnState?.groupIndex === "number" ? (
            <Item
              key="ungroup"
              icon="mdi:ungroup"
              label={ctx.t("gridUngroup")}
              onClick={run(() => ctx.ungroup(column))}
            />
          ) : (
            <Item
              key="group"
              icon="mdi:format-list-group"
              label={ctx.t("gridGroupByColumn")}
              onClick={run(() => ctx.group(column))}
            />
          ),
          ctx.groupCount > 0 ? (
            <Item
              key="ungroupAll"
              icon="mdi:format-list-bulleted"
              label={ctx.t("gridUngroupAll")}
              onClick={run(() => ctx.ungroupAll())}
            />
          ) : null,
        ].filter(Boolean)
      );

    if (ctx.columnFixingEnabled)
      sections.push([
        <Item
          key="fixLeft"
          icon="mdi:pin-outline"
          label={ctx.t("gridFixLeft")}
          selected={columnState?.fixed && columnState?.fixedPosition !== "right"}
          onClick={run(() => ctx.fixColumn(column, "left"))}
        />,
        <Item
          key="fixRight"
          icon="mdi:pin-outline"
          label={ctx.t("gridFixRight")}
          selected={columnState?.fixed && columnState?.fixedPosition === "right"}
          onClick={run(() => ctx.fixColumn(column, "right"))}
        />,
        <Item
          key="unfix"
          icon="mdi:pin-off-outline"
          label={ctx.t("gridUnfix")}
          disabled={!columnState?.fixed}
          onClick={run(() => ctx.fixColumn(column, null))}
        />,
      ]);

    const layout = [];

    if (ctx.allowColumnResizing && column.allowResizing)
      layout.push(
        <Item
          key="autofit"
          icon="mdi:arrow-expand-horizontal"
          label={ctx.t("gridAutoFit")}
          onClick={run(() => ctx.autoFitColumn(column))}
        />
      );

    if (ctx.columnChooserEnabled) {
      if (column.allowHiding)
        layout.push(
          <Item
            key="hide"
            icon="mdi:eye-off-outline"
            label={ctx.t("gridHideColumn")}
            onClick={run(() => ctx.hideColumn(column))}
          />
        );

      layout.push(
        <Item
          key="chooser"
          icon="mdi:table-column-plus-after"
          label={ctx.t("gridColumnChooser")}
          onClick={run(() => ctx.openColumnChooser())}
        />
      );
    }

    if (layout.length) sections.push(layout);
  }

  const content = sections.flatMap((section, index) =>
    index === 0 ? section : [<Divider key={`divider${index}`} />, ...section]
  );

  return (
    <Menu
      open={!!target && content.length > 0}
      onClose={onClose}
      anchorEl={target?.position ? undefined : target?.anchorEl}
      anchorReference={target?.position ? "anchorPosition" : "anchorEl"}
      anchorPosition={target?.position}
      slotProps={{ paper: { sx: { minWidth: 220 } } }}
    >
      {content}
    </Menu>
  );
}

ColumnMenu.propTypes = {
  target: PropTypes.object,
  ctx: PropTypes.object,
  onClose: PropTypes.func,
};
